import { useSignIn } from '@clerk/clerk-react';
import { useState, type FormEvent } from 'react';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import { Alert } from '../ui/Alert';
import { InstallBanner } from '../pwa/InstallBanner';

/**
 * Device login.
 *
 * The credential is a device, not a person — `str0001_billing` rather than an
 * email address — so this is a plain username/password form against Clerk with
 * no sign-up link, no social buttons and no "forgot password". None of those
 * can work: public sign-up is disabled, and Clerk's self-service reset is email
 * based and cannot reach an account with no email. A forgotten device password
 * is recovered by the store owner from the admin dashboard.
 *
 * Signing in here revokes any other live session for the same credential — one
 * credential, one active device.
 */
export function LoginScreen() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!isLoaded || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await signIn.create({ identifier: username.trim(), password });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
      } else {
        // No second factor is configured for device credentials, so anything
        // other than `complete` is a configuration problem worth naming.
        setError('Sign-in could not be completed. Please tell the provider.');
      }
    } catch {
      // Clerk returns the same shape for a wrong username and a wrong password,
      // and it should stay that way — distinguishing them tells an attacker
      // which half they got right.
      setError('That username or password is not right.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-canvas px-6 pt-safe pb-safe">
      <div className="mx-auto w-full max-w-sm">
        <h1 className="text-3xl font-bold text-primary">Shringaar</h1>
        <p className="mt-2 text-sm text-secondary">Sign in with this device's credentials.</p>

        <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
          <TextField
            label="Device username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            required
            placeholder="str0001_billing"
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />

          {error && <Alert tone="danger">{error}</Alert>}

          <Button type="submit" loading={submitting} disabled={!isLoaded} fullWidth>
            Sign in
          </Button>
        </form>

        <div className="mt-8">
          <InstallBanner />
        </div>
      </div>
    </div>
  );
}
